/* eslint-disable @typescript-eslint/ban-ts-comment */

/* eslint-disable @typescript-eslint/prefer-ts-expect-error */
import {
  checkNotamActiveByTime,
  checkNotamEffectiveByTimeWorklet
} from './notam';
import {IAirport, INotamAI} from '@/firebase/airport';
import store from '@/redux/store';
import {IFlightItem} from '@/types/flight';
import moment from 'moment';
import {toast} from 'react-toastify';

type Factor = Record<string, number>;

type StationWithScore = Record<string, Factor>;

export enum DiversionType {
  TECH_STOP = 'techStop',
  OPERATION = 'operation',
  AIRCRAFT_AMBER = 'aircraftAmber',
  AIRCRAFT_RED = 'aircraftRed',
  AIRCRAFT_MEDICAL = 'aircraftMedical'
}

export const SCORE_GROUPS = {
  Distance: ['distance'],
  Airport: [
    'arrDelay',
    'airportClose',
    'isDepartureDestination',
    'isEnroute',
    'isDepartureCountry',
    'isDestinationCountry'
  ],
  Weather: ['visibility', 'wind'],
  RunwayApproach: [
    'approachUS',
    'runwayFavourability',
    'runwayPCN',
    'runwayLength',
    'runwayClose'
  ]
};

export const DIVERSION_OPTIONS = [
  {
    value: DiversionType.TECH_STOP,
    label: 'Tech Stop'
  },
  {
    value: DiversionType.OPERATION,
    label: 'Operational'
  },
  {
    value: DiversionType.AIRCRAFT_AMBER,
    label: 'Aircraft–Amber'
  },
  {
    value: DiversionType.AIRCRAFT_RED,
    label: 'Aircraft–Red'
  },
  {
    value: DiversionType.AIRCRAFT_MEDICAL,
    label: 'Medical'
  }
];

export interface IStationWithScore {
  score: number;
  scoreGroups: ScoreGroups;
  scores: Scores;
  station: string;
  period?: any;
  distance?: number;
}

export interface ScoreGroups {
  Airport: number;
  Approach: number;
  Runway: number;
  Weather: number;
}

export interface Scores {
  airportClose: number;
  arrDelay: number;
  isDeparture: number;
  isEnroute: number;
  isDepartureCountry?: number;
  isDestinationCountry?: number;
  isCompanyStation?: number;
  approachUS: number;
  runwayClose: number;
  runwayPCN: number;
  runwayLength: number;
  runwayFavourability: number;
  visibility: number;
  wind: number;
}

export const getDiversionTypeLabel = (type: DiversionType) => {
  return DIVERSION_OPTIONS.find((option) => option.value === type)?.label;
};

const techStopFactors: Factor = {
  distance: 0.13,
  visibility: 0.12,
  wind: 0.05,
  runwayFavourability: 0.1,
  arrDelay: 0.04,
  airportClose: 0.18,
  runwayClose: 0.1,
  isDepartureDestination: 0.14,
  isEnroute: 0.09,
  approachUS: 0.05
};

const operationFactors: Factor = {
  distance: 0.13,
  visibility: 0.12,
  wind: 0.05,
  runwayFavourability: 0.1,
  arrDelay: 0.04,
  airportClose: 0.18,
  runwayClose: 0.1,
  isDepartureDestination: 0.14,
  isDepartureCountry: 0.05,
  isDestinationCountry: 0.05,
  approachUS: 0.04
};

const aircraftAmberFactors: Factor = {
  distance: 0.14,
  visibility: 0.12,
  wind: 0.06,
  runwayFavourability: 0.1,
  runwayLength: 0.11,
  runwayPCN: 0.04,
  airportClose: 0.15,
  runwayClose: 0.05,
  approachUS: 0.05,
  isDepartureDestination: 0.13,
  isDepartureCountry: 0.025,
  isDestinationCountry: 0.025
};

const aircraftRedMedicalFactors: Factor = {
  distance: 0.14,
  visibility: 0.13,
  wind: 0.06,
  runwayFavourability: 0.12,
  runwayLength: 0.11,
  runwayPCN: 0.01,
  airportClose: 0.15,
  runwayClose: 0.05,
  approachUS: 0.05,
  isDepartureDestination: 0.13,
  isCompanyStation: 0.05
};

export const getAirport = (name: string) => {
  const airport = store
    .getState()
    .airport.airportData.find((airport) => airport.airport === name);
  if (!airport) {
    // toast.error('Airport not found' + name);
    return null;
  }
  return airport;
};

export const airportColor = (color: string) => {
  'worklet';
  switch (color) {
    case 'black':
      return '#000';
    case 'yellow':
      return '#FC0';
    case 'green':
      return '#1DB954';
    case 'red':
      return '#F04438';
    case 'blue':
      return '#007AFF';
    case 'amber':
      return '#FF9500';
    default:
      return '#1DB954';
  }
};

export const getAirportGeoJSON = (name: string) => {
  const item = getAirport(name);
  if (!item) return null;
  return {
    type: 'Feature',
    properties: {
      id: item.airport,
      airport: item.airport,
      colour: item?.colour ?? '',
      textColor: airportColor(item.colour),
      not_key_icao: item?.not_key_icao ?? false
    },
    geometry: {
      type: 'Point',
      coordinates: [
        parseFloat(parseFloat(item?.long ?? '').toPrecision(6)),
        parseFloat(parseFloat(item?.lat ?? '').toPrecision(6))
      ]
    }
  };
};

export const getAirportIata = (name: string) => {
  const airport = getAirport(name);
  if (!airport) return null;
  return airport.iata;
};

const getTafMatchingPeriod = (airport: IAirport) => {
  const currentTime = moment.utc().format('YYYY-MM-DD HH:mm');
  const periods = airport?.taf_new?.periods ?? [];
  const periosWithinTime = periods.filter(
    (period: any) =>
      moment.utc(period.start).isSameOrBefore(moment.utc(currentTime)) &&
      moment.utc(period.end).isSameOrAfter(moment.utc(currentTime))
  );
  if (periosWithinTime?.length === 0) return null;
  return periosWithinTime[0];
};

// if visibility < 1 return 1
// if visibility < 3 return 2
// if visibility < 5 return 3
// if visibility > 5 return 4
// if visibility === 9999 return 5
// else return 0
// Confirm: matching period in taf_new with current time
const getVisibilityScore = (airport: IAirport) => {
  // Score 1	Score 2	Score 3	Score 4	Score 5
  // visibility < 1	visibility < 3	visibility < 5	visibility > 5	visibility == 9999
  const period = getTafMatchingPeriod(airport);
  const visibility = period?.visibility ?? null;
  console.log('getVisibilityScore', airport.airport, visibility);
  if (visibility === 9999) return 5;
  if (visibility > 5) return 4;
  if (visibility < 1) return 1;
  if (visibility < 3) return 2;
  if (visibility < 5) return 3;
  return 0;
};

// if wind > 10 return 1
// if wind > 5 return 3
// if wind === 0 or wind === '' return 5
// else return 0
// Confirm: wind_gust_kt, when it is empty, it's 0
const getWindScore = (airport: IAirport) => {
  const period = getTafMatchingPeriod(airport);
  const wind = period?.wind_gust_kt ?? null;
  console.log('getWindScore', airport.airport, wind);
  if (wind > 10) return 1;
  if (wind > 5) return 3;
  if (wind === 0 || wind === '') {
    return 5;
  }
  return 0;
};

// if runway length is 0 return 1
// if runway length is 1 return 3
// if runway length > 1 return 5
// else return 0
// Confirm: count by wind_runway_data > runway > favourability === 'Favourable'
// when data is not available, return 3, 1 favourable is 4
const getRunwayFavourabilityScore = (airport: IAirport) => {
  const period = getTafMatchingPeriod(airport);
  let length = 0;
  const runway = period?.wind_runway_data ?? {};
  console.log(
    'getRunwayFavourabilityScore',
    airport.airport,
    JSON.stringify(runway)
  );
  if (!period?.wind_runway_data) {
    return 3;
  }
  Object.keys(runway).forEach((key) => {
    const runwayData = runway[key];
    if (['Less favourable', 'Favourable'].includes(runwayData?.favourability)) {
      length += 1;
    }
  });
  if (length === 0) return 1;
  if (length === 1) return 4;
  if (length > 1) return 5;
  return 0;
};

// if arrival delay > 30 return 1
// if arrival delay > 15 return 2
// if arrival delay > 10 return 3
// if arrival delay > 5 return 4
// else return 5
const getArrDelayScore = (airport: IAirport) => {
  const delay = parseInt(airport?.arr_delay ?? '0');
  console.log('getArrDelayScore', airport.airport, delay);
  if (delay > 30) return 1;
  if (delay > 15) return 2;
  if (delay > 10) return 3;
  if (delay > 5) return 4;
  else if (delay <= 5) return 5;
};

// if departure is the same as the airport return 5, else return 1
const getIsDepartureDestinationScore = (
  airport: IAirport,
  sector: any,
  type: DiversionType
) => {
  if (
    sector?.depICAO === airport?.airport ||
    sector?.arrICAO === airport?.airport
  ) {
    return 5;
  }
  if (
    [
      DiversionType.AIRCRAFT_AMBER,
      DiversionType.AIRCRAFT_RED,
      DiversionType.AIRCRAFT_MEDICAL
    ].includes(type)
  ) {
    return 4;
  }
  return 1;
};

const EARTH_RADIUS_NM = 3440.065; // Nautical miles

const toRadians = (deg: number) => {
  'worklet';
  return deg * (Math.PI / 180);
};
function bearing(
  from: {lat: number; lon: number},
  to: {lat: number; lon: number}
) {
  'worklet';
  const φ1 = toRadians(from.lat);
  const φ2 = toRadians(to.lat);
  const Δλ = toRadians(to.lon - from.lon);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = Math.atan2(y, x);
  return ((θ * 180) / Math.PI + 360) % 360;
}

// Helper function to calculate distance between two lat/lon points using Haversine formula
export const haversineDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) => {
  'worklet';
  const R = 3440.065; // radius of Earth in nautical miles
  const toRadians = (angle: number) => (angle * Math.PI) / 180;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

function crossTrackDistance(
  place: {lat: number; lon: number},
  start: {lat: number; lon: number},
  end: {lat: number; lon: number}
) {
  'worklet';
  const d13 =
    haversineDistance(start.lat, start.lon, place.lat, place.lon) /
    EARTH_RADIUS_NM;
  const θ13 = toRadians(bearing(start, place));
  const θ12 = toRadians(bearing(start, end));
  const δxt = Math.asin(Math.sin(d13) * Math.sin(θ13 - θ12));
  return Math.abs(δxt * EARTH_RADIUS_NM);
}

function isAhead(
  place: {lat: number; lon: number},
  start: {lat: number; lon: number},
  end: {lat: number; lon: number}
) {
  'worklet';
  const d13 =
    haversineDistance(start.lat, start.lon, place.lat, place.lon) /
    EARTH_RADIUS_NM;
  const θ13 = toRadians(bearing(start, place));
  const θ12 = toRadians(bearing(start, end));
  const δxt = Math.asin(Math.sin(d13) * Math.sin(θ13 - θ12));
  const δat = Math.acos(Math.cos(d13) / Math.cos(δxt));
  const alongTrackDistance = δat * EARTH_RADIUS_NM;
  return alongTrackDistance >= 0;
}

const getIsEnrouteScore = (airport: IAirport, airportsOnTheWay: string[]) => {
  if (airportsOnTheWay.includes(airport.airport)) return 5;
  return 1;
};

// if departure country is the same as the airport country return 5, else return 1
const getIsDepartureCountryScore = (airport: IAirport, sector: any) => {
  const depCountry = getAirport(sector?.depICAO as string)?.country_code;
  const airportCountry = airport?.country_code;
  if (depCountry === airportCountry) return 5;
  return 1;
};

// if destination country is the same as the airport country return 5, else return 1
const getIsDestinationCountryScore = (airport: IAirport, sector: any) => {
  const arrCountry = getAirport(sector?.arrICAO as string)?.country_code;
  const airportCountry = airport?.country_code;
  if (arrCountry === airportCountry) return 5;
  return 1;
};

export const calculateRunwayLength = (airport: IAirport) => {
  const data = airport?.runway ?? [];
  let length = 0;
  for (let i = 0; i < data?.length; i++) {
    const runway = data[i];
    if (typeof runway?.length === 'number' && runway.length > length) {
      length = runway.length;
    } else if (typeof runway?.length === 'string') {
      const tempLength = parseInt(runway.length);
      if (tempLength > length) {
        length = tempLength;
      }
      // @ts-ignore
    } else if (typeof runway?.lda === 'number' && runway.lda > length) {
      // @ts-ignore
      length = runway.lda;
      // @ts-ignore
    } else if (typeof runway?.lda === 'string') {
      // @ts-ignore
      const tempLength = parseInt(runway.lda);
      if (tempLength > length) {
        length = tempLength;
      }
    }
  }
  return length;
};

// if runway length < 1000 return 1
// else if runway length < 2000 return 2
// else if runway length < 3000 return 3
// else if runway length < 4000 return 4
// else return 5
// Confirm: runway length: length or lda field
const getRunwayLengthScore = (airport: any) => {
  const length = calculateRunwayLength(airport);
  if (length < 1000) return 1;
  if (length < 2000) return 2;
  if (length < 3000) return 3;
  if (length < 4000) return 4;
  return 5;
};

export const calculateRunwayPCN = (airport: any) => {
  const data = airport?.runway ?? [];
  let pcn = -1;
  for (let i = 0; i < data?.length; i++) {
    const runway = data[i];
    if (typeof runway?.pcn === 'undefined') {
      continue;
    }
    const pcnValue = parseInt(runway?.pcn?.toString() ?? '0');
    if (pcnValue > pcn) {
      pcn = pcnValue;
    }
  }
  if (pcn === -1) {
    return 'no data';
  }
  if (pcn.toString().length >= 3) {
    // remove the last 1 digit
    pcn = parseInt(pcn.toString().slice(0, -1));
  }
  return pcn;
};

const getAircraftBasicData = (aircraft: string) => {
  return window.aircraftGroup?.[aircraft] ?? {};
};

// if pcn is not available return 3
// if pcn < aircraftCN return 1, else return 5
const getRunwayPCNScore = (airport: any, aircraft: string) => {
  const pcn = calculateRunwayPCN(airport);
  // aircraftCN to get from rtdb for the aircraft type
  const aircraftCN = getAircraftBasicData(aircraft)?.acn ?? 0;
  if (!pcn || pcn === 'no data') return 3;
  if (pcn < aircraftCN) return 1;
  else return 5;
};

// if match return notam and store is 1, else return 5
const getApproachUSScore = (notams: any[]) => {
  // search ILS/RNP/RNAV/VOR/NDB + UNSERVICEABLE/'U/S'/NOT AVAIL/INOP
  const matchOne = notams.filter((notam) => {
    return (
      notam.category === 'Approach' &&
      (notam.content.includes('ILS') ||
        notam.content.includes('RNP') ||
        notam.content.includes('RNAV') ||
        notam.content.includes('VOR') ||
        notam.content.includes('NDB'))
    );
  });
  if (matchOne.length === 0) return 5;
  const matchTwo = matchOne.filter((notam) => {
    return (
      notam.content.includes('UNSERVICEABLE') ||
      notam.content.includes('U/S') ||
      notam.content.includes('NOT AVAIL') ||
      notam.content.includes('INOP')
    );
  });
  if (matchTwo.length === 0) return 5;
  else return matchTwo;
};

// if match return notam and store is 1, else return 5
const getAirportCloseScore = (notams: any[]) => {
  // search AERODROME CLOSED
  // AERODROME CLOSURE
  // AIRPORT CLOSED
  // AIRPORT CLOSURE
  const excludes = [
    'STAND',
    'GATE',
    'PARKING',
    'TAXIWAY',
    'TAXILANE',
    'APRON',
    'SPOT'
  ];
  const isMatchExcludes = (content: string) => {
    return excludes.some((exclude) => content.includes(exclude));
  };
  const match = notams.filter((notam) => {
    return (
      notam.category === 'Others' &&
      (notam.content.includes('AERODROME CLOSED') ||
        notam.content.includes('AERODROME CLOSURE') ||
        notam.content.includes('AIRPORT CLOSED') ||
        notam.content.includes('AIRPORT CLOSURE')) &&
      !isMatchExcludes(notam.content)
    );
  });
  console.log('getAirportCloseScore', match.length);
  if (match.length === 0) return 5;
  else return match;
};

// if match return notam and store is 1, else return 5
const getRunwayCloseScore = (notams: any[]) => {
  // search RUNWAY CLOSED
  // RUNWAY CLOSURE
  const match = notams.filter((notam) => {
    return (
      notam.category === 'Runway' &&
      (notam.content.includes('RUNWAY CLOSED') ||
        notam.content.includes('RUNWAY CLOSURE'))
    );
  });
  console.log('getRunwayCloseScore', match.length);
  if (match.length === 0) return 5;
  else return match;
};

export const getUserAirlineStations = () => {
  const airline = localStorage.getItem('airline');
  const airlineWhitelist = window.airlineWhiteList?.[airline as string];
  const stations = airlineWhitelist?.stations ?? [];
  return {
    stations,
    logo: airlineWhitelist?.logo
  };
};

export const getUserAirlineWhitelist = () => {
  const airline = localStorage.getItem('airline');
  const airlineWhitelist = window.airlineWhiteList?.[airline as string];
  return airlineWhitelist;
};

// to calculate distance of station to present position
// use haversine distance between present lat long and airport lat long
export const getDiversionStations = async (
  type: DiversionType,
  currentLat: number,
  currentLon: number,
  speed: number,
  arrLat: number,
  arrLon: number
) => {
  const airline = localStorage.getItem('airline');
  const airlineWhitelist = getUserAirlineWhitelist();
  const stations = airlineWhitelist?.stations ?? [];
  const maxCrossTrackNm = 300; // miles
  const airports = store.getState().airport.airportData;
  const airportsWithDistance = [];
  const airportsOnTheWay = [];
  // change to get all airport in radius: 300 miles
  for (let i = 0; i < airports.length; i++) {
    const airport = airports[i];
    // get distance between current position and airport position
    const distance = haversineDistance(
      currentLat,
      currentLon,
      parseFloat(airport.lat),
      parseFloat(airport.long)
    );
    let isMatch = false;
    // radius dynamic distance
    if (type === DiversionType.TECH_STOP) {
      // all company stations within 4hr radius
      if (!stations.includes(airport.airport)) {
        continue;
      }
      // current speed(470 if not avail) * 4hr
      if (distance <= speed * 4) {
        isMatch = true;
      }
    } else if (type === DiversionType.OPERATION) {
      // all company stations
      if (!stations.includes(airport.airport)) {
        continue;
      }
      isMatch = true;
    } else if (
      [
        DiversionType.AIRCRAFT_AMBER,
        DiversionType.AIRCRAFT_RED,
        DiversionType.AIRCRAFT_MEDICAL
      ].includes(type)
    ) {
      // all stations within 2hr radius
      if (distance <= speed * 2) {
        isMatch = true;
      }
    }
    if (!isMatch) continue;
    // add to the list of airportsWithDistance
    airportsWithDistance.push({
      ...airport,
      airlines: stations.includes(airport.airport) ? airline : '',
      isCompanyStation: stations.includes(airport.airport),
      distance
    });
    const place = {
      lat: parseFloat(airport.lat),
      lon: parseFloat(airport.long)
    };
    const start = {
      lat: currentLat,
      lon: currentLon
    };
    const end = {
      lat: arrLat,
      lon: arrLon
    };
    const xt = crossTrackDistance(place, start, end);
    const ahead = isAhead(place, start, end);
    if (ahead && xt <= maxCrossTrackNm) {
      airportsOnTheWay.push(airport.airport);
    }
  }
  // sort the list of airportsWithDistance by distance
  airportsWithDistance.sort((a, b) => a.distance - b.distance);
  return {
    airportsWithDistance,
    airportsOnTheWay
  };
};

const getNotamByAirport = (airport: string) => {
  return window.notamByAirport[airport] ?? [];
};

// get notam of airport by range of time
const getNotamOfAirportByTime = (
  airport: string,
  startTime: Date,
  endTime: Date
) => {
  const notams = getNotamByAirport(airport) ?? [];
  const result = [];
  if (!notams) return [];
  for (let i = 0; i < notams?.length; i++) {
    const notam = notams[i];
    notam.active = checkNotamActiveByTime(notam, startTime, endTime);
    const isEffective = checkNotamEffectiveByTimeWorklet(
      notam,
      startTime,
      endTime
    );
    if (isEffective) {
      result.push(notam);
    }
  }
  return result;
};

const getIsCompanyStationScore = (airport: any) => {
  if (airport?.isCompanyStation) return 5;
  return 1;
};

const getDistanceScore = (distance: number, speed: number) => {
  const timeOfSpeed = distance / speed;
  //   Score 1	Score 2	Score 3	Score 4	Score 5
  // >120min	90-120min	60-90min	30-60min	<30min
  if (timeOfSpeed > 120) return 1;
  if (timeOfSpeed >= 90 && timeOfSpeed <= 120) return 2;
  if (timeOfSpeed >= 60 && timeOfSpeed <= 90) return 3;
  if (timeOfSpeed >= 30 && timeOfSpeed <= 60) return 4;
  return 5;
};

const DIVERSION_TYPE_FACTORS = {
  [DiversionType.TECH_STOP]: techStopFactors,
  [DiversionType.OPERATION]: operationFactors,
  [DiversionType.AIRCRAFT_AMBER]: aircraftAmberFactors,
  [DiversionType.AIRCRAFT_RED]: aircraftRedMedicalFactors,
  [DiversionType.AIRCRAFT_MEDICAL]: aircraftRedMedicalFactors
};

const DIVERSION_SCORE_FUNCTIONS: any = {
  distance: getDistanceScore,
  visibility: getVisibilityScore,
  wind: getWindScore,
  runwayFavourability: getRunwayFavourabilityScore,
  arrDelay: getArrDelayScore,
  isDepartureDestination: getIsDepartureDestinationScore,
  isEnroute: getIsEnrouteScore,
  isDepartureCountry: getIsDepartureCountryScore,
  isDestinationCountry: getIsDestinationCountryScore,
  runwayLength: getRunwayLengthScore,
  runwayPCN: getRunwayPCNScore,
  isCompanyStation: getIsCompanyStationScore,
  approachUS: getApproachUSScore,
  airportClose: getAirportCloseScore,
  runwayClose: getRunwayCloseScore
};

function milesPerMinToMilesPerHour(milesPerMin: number) {
  // There are 60 minutes in an hour.
  const milesPerHour = milesPerMin * 60;
  return milesPerHour;
}

export const getDiversionSuggestionRank = (
  stationWithScore: StationWithScore,
  inputFactors: Factor
) => {
  'worklet';
  // Define the factors
  const factors = Object.keys(inputFactors);

  // Define weights for each factor (must sum to 1)
  const weights = inputFactors;

  // Define scores for each station on each factor (1 to 5)
  const scores = stationWithScore;

  // Function to calculate weighted score for each station
  function calculateWeightedScores(scores: StationWithScore, weights: Factor) {
    'worklet';
    const results: IStationWithScore[] = [];

    for (const station in scores) {
      let total = 0;
      const scoreGroups: Record<string, number> = {
        Distance: 0,
        Airport: 0,
        Weather: 0,
        RunwayApproach: 0
      };
      const tempScoreGroups: Record<string, Record<string, number>> = {
        Distance: {
          distance: 0
        },
        Airport: {
          arrDelay: 0,
          airportClose: 0,
          isDepartureDestination: 0,
          isEnroute: 0,
          isDepartureCountry: 0,
          isDestinationCountry: 0
        },
        Weather: {
          visibility: 0,
          wind: 0
        },
        RunwayApproach: {
          runwayFavourability: 0,
          runwayPCN: 0,
          runwayLength: 0,
          runwayClose: 0,
          approachUS: 0
        }
      };
      for (const factor of factors) {
        const score = scores[station][factor];
        if (!score) continue;
        const weight = weights[factor];
        total += score * weight;
        if (SCORE_GROUPS.Airport.includes(factor)) {
          tempScoreGroups.Airport[factor] = score;
        }
        if (SCORE_GROUPS.Weather.includes(factor)) {
          tempScoreGroups.Weather[factor] = score;
        }
        if (SCORE_GROUPS.RunwayApproach.includes(factor)) {
          tempScoreGroups.RunwayApproach[factor] = score;
        }
        if (factor === 'distance') {
          tempScoreGroups.Distance[factor] = score;
        }
      }
      Object.keys(tempScoreGroups).forEach((group) => {
        const temp = tempScoreGroups[group];
        let score = 0;
        let length = 0;
        // if group is not Airport, calculate the score for the group
        if (group !== 'Airport') {
          Object.keys(temp).forEach((factor) => {
            if (!temp[factor]) return;
            score += temp[factor];
            length += 1;
          });
          scoreGroups[group] = length > 0 ? score / length : 0;
          return;
        }
        const isDepartureDestination =
          scores[station]?.isDepartureDestination === 5;
        const isEnroute = scores[station]?.isEnroute === 5;
        const isDepartureCountry = scores[station]?.isDepartureCountry === 5;
        const isDestinationCountry =
          scores[station]?.orgIsDestinationCountry === 5;
        // if isDepartureDestination === 5, ignore isEnroute
        // if isEnroute === 5, ignore isDepartureDestination
        // if isDepartureCountry === 5, ignore isDestinationCountry
        // if isDestinationCountry === 5, ignore isDepartureCountry
        Object.keys(temp).forEach((factor) => {
          if (!temp[factor]) return;
          if (factor === 'isEnroute' && isDepartureDestination) return;
          if (factor === 'isDepartureDestination' && isEnroute) return;
          if (factor === 'isDepartureCountry' && isDestinationCountry) return;
          if (factor === 'isDestinationCountry' && isDepartureCountry) return;
          score += temp[factor];
          length += 1;
        });
        scoreGroups[group as keyof typeof SCORE_GROUPS] =
          length > 0 ? score / length : 0;
      });
      results.push({
        station,
        distance: scores[station]?.distance,
        scoreGroups: scoreGroups as unknown as ScoreGroups,
        score: parseFloat(total.toFixed(2)),
        period: scores[station]?.period,
        scores: {
          ...(scores[station] as unknown as Scores),
          // @ts-ignore
          period: undefined
        }
      });
    }
    // sort by score and distance
    return results.sort((a, b) => {
      if (b.score === a.score) {
        // @ts-ignore
        return a.distance - b.distance;
      }
      return b.score - a.score;
    });
  }

  // Run and display the results
  const rankedStations = calculateWeightedScores(scores, weights);
  return rankedStations;
};

// https://docs.google.com/spreadsheets/d/1qPK2Fda5Pu5NjdAUDmPl23dKsLOIJk3HECmde9iGE98/edit?gid=0#gid=0
export const getDiversionRankingForFlight = async (
  flight: IFlightItem,
  currentLat: number,
  currentLon: number,
  type: DiversionType
) => {
  // to get notam from now to now +4 hours
  const now = new Date(moment.utc().format('YYYY-MM-DD HH:mm:ss'));
  const maxDate = new Date(
    moment.utc().add(4, 'hours').format('YYYY-MM-DD HH:mm:ss')
  );
  const departure = window?.airports?.find((i) => i.code === flight.depIATA);
  const arrival = window?.airports?.find((i) => i.code === flight.destIATA);
  if (!arrival) {
    toast.info('Arrival information not found');
    return null;
  }
  // parse '(148.95305 -5.971389)'
  const arrivalLocation = arrival.location
    ?.replace('(', '')
    .replace(')', '')
    .split(' ');
  if (!arrivalLocation) {
    toast.info('Arrival location not found');
    return null;
  }
  const arrivalLat = parseFloat(arrivalLocation[0]);
  const arrivalLon = parseFloat(arrivalLocation[1]);
  if (!arrivalLat || !arrivalLon) {
    toast.info('Arrival location not found');
    return null;
  }
  const sector = {
    arrICAO: arrival?.icao,
    depICAO: departure?.icao
  };
  if (!sector) {
    console.log('getDiversionRankingForFlight', 'sector not found');
    return null;
  }
  const speed = 5.89423;
  // TODO: correct data
  const speedInMilesPerHour = 353.65;
  console.log('speedInMilesPerHour', speedInMilesPerHour);
  // get list of stations
  const {airportsWithDistance: stations, airportsOnTheWay} =
    await getDiversionStations(
      type,
      currentLat,
      currentLon,
      speedInMilesPerHour,
      arrivalLat,
      arrivalLon
    );
  if (stations.length === 0) {
    toast.info('No matching station');
    console.log('getDiversionRankingForFlight', 'stations not found');
    return null;
  }
  const stationWithScore: Record<string, Factor> = {};
  const factors = DIVERSION_TYPE_FACTORS[type];
  const stationNotamsAware: Record<string, Record<string, INotamAI[]>> = {};
  // loop through stations
  for (let i = 0; i < stations?.length; i++) {
    const station = stations[i];
    const airport = station.airport;
    if (!stationWithScore[airport]) {
      stationWithScore[airport] = {};
    }
    if (!stationNotamsAware[airport]) {
      stationNotamsAware[airport] = {};
    }
    const period = getTafMatchingPeriod(station);
    stationWithScore[airport].period = period;
    stationWithScore[airport].distance = station.distance;
    // get notam of airport by range of time
    const notams = getNotamOfAirportByTime(airport, now, maxDate);
    Object.keys(factors).forEach((factor) => {
      const func = DIVERSION_SCORE_FUNCTIONS[factor];
      if (['airportClose', 'runwayClose', 'approachUS'].includes(factor)) {
        const score = func(notams);
        if (typeof score === 'number') {
          stationWithScore[airport][factor] = score;
        } else {
          stationWithScore[airport][factor] = 1;
          stationNotamsAware[airport][factor] = score;
        }
      } else if (factor === 'isEnroute') {
        const score = func(station, airportsOnTheWay);
        if (typeof score === 'number') {
          stationWithScore[airport][factor] = score;
        }
      } else if (factor === 'distance') {
        const score = func(station.distance, speed);
        if (typeof score === 'number') {
          stationWithScore[airport][factor] = score;
        }
      } else {
        const score = func(station, sector, type);
        stationWithScore[airport][factor] = score;
      }
      if (typeof stationWithScore[airport].isDestinationCountry === 'number') {
        stationWithScore[airport].orgIsDestinationCountry =
          stationWithScore[airport].isDestinationCountry;
      }
      // special case for Operation and Aircraft-amber: if the airport isDeparture then set isDestinationCountry to 5
      // it will help to generate the score is 5/5 instead of 4.9/5
      if (
        stationWithScore[airport].isDepartureDestination === 5 &&
        typeof stationWithScore[airport].isDestinationCountry === 'number'
      ) {
        stationWithScore[airport].isDestinationCountry = 5;
      }
    });
  }
  const result = getDiversionSuggestionRank(stationWithScore, factors);
  return {
    stations: result,
    stationNotamsAware,
    now,
    maxDate,
    sector
  };
};

export const getFactorColor = (score: number) => {
  if (score >= 0 && score < 2) {
    // red
    return '#DC2626';
  }
  if (score >= 2 && score <= 4) {
    // amber
    return '#D97706';
  }
  return '#16A34A';
};

export const getFactorColorWithNoData = (
  score: number | undefined | string
) => {
  if (typeof score === 'string' && score === 'no data') {
    return '#FF9500';
  }
  if (typeof score === 'undefined') {
    return '#ABA29E';
  }
  if (typeof score === 'number') {
    return getFactorColor(score);
  }
  return '#FF9500';
};
